import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, AfterViewChecked, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';
import { UserService } from 'src/app/shared/services/user.service';
import { MinimizedMeetingService } from '../services/minimized-meeting.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { AzureSpeechService } from 'src/app/shared/services/azure-speech.service';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { MessageDialogComponent } from 'src/app/shared/components/message-dialog/message-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { MeetingSummaryDialogComponent } from '../meeting-summary-dialog/meeting-summary-dialog.component';
import { AssignEvaluatorsComponent } from '../../evaluations/assign-evaluators/assign-evaluators.component';
import { BidDocumentsDialogComponent } from '../bid-documents-dialog/bid-documents-dialog.component';

interface AgendaBidUnsealDocument {
  attachmentId?: string;
  attachmentFor?: string;
  name?: string;
  fileContent?: string;
  filePath?: string;
  url?: string;
  downloadUrl?: string;
  mimeType?: string;
  fileSize?: number;
  documentExpiryDate?: string;
  isUnsealed: boolean;
}

interface AgendaBidUnsealItem {
  bidId: string;
  vendorId?: string;
  vendorName?: string;
  bidAmount?: number;
  bidValidTill?: string;
  hasTechnicalDocument: boolean;
  technicalUnsealed: boolean;
  hasCommercialDocument: boolean;
  commercialUnsealed: boolean;
  technicalEvaluationDecision?: string;
  isTechnicallyRejected?: boolean;
  evaluations?: Array<{ evaluationId?: string; evaluationType?: string; status?: string; decision?: string; comments?: string; rejectionReason?: string }>;
  technicalDocuments?: AgendaBidUnsealDocument[];
  commercialDocuments?: AgendaBidUnsealDocument[];
}

interface UnsealVoteView {
  bidId: string;
  userId: string;
  userName?: string;
  documentScope: string;
  votedAt?: string;
}

@Component({
  selector: 'app-admin-meeting-dialog',
  templateUrl: './admin-meeting-dialog.component.html',
  styleUrls: ['./admin-meeting-dialog.component.scss']
})
export class AdminMeetingDialogComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  @ViewChild('agendaItemsList', { static: false }) agendaItemsList!: ElementRef;
  @ViewChild('tcDecisionEnEditor') tcDecisionEnEditor?: ElementRef<HTMLDivElement>;
  @ViewChild('tcDecisionArEditor') tcDecisionArEditor?: ElementRef<HTMLDivElement>;
  decisionForm: FormGroup;
  meetingTitle: string = '';
  meetingId: string = '';
  agendaItems: any[] = [];
  currentAgendaIndex: number = 0;
  agendaDecisions: Map<number, { tcDecisionEn: string; tcDecisionAr: string }> = new Map();
  attendees: Array<{ userId: string; userName: string }> = [];
  // Store meeting members with their roles
  meetingMembers: Array<{ userId: string; userName: string; role: string }> = [];
  // Store members' answers for each agenda item: agendaIndex -> Array<{userId, userName, action, comment}>
  membersAnswers: Map<number, Array<{ userId: string; userName: string; action: string; comment: string }>> = new Map();
  private shouldScroll: boolean = false;
  private subscriptions: Subscription[] = [];
  private isMinimizing: boolean = false;
  private summaryDialogOpened: boolean = false; // Flag to prevent opening summary dialog twice
  isTranslating: boolean = false; // Flag for translation status
  currentAgendaItemAttachments: Array<{ name: string; originalName?: string; url: string; size: string; fileType: string }> = [];
  isLoadingAttachments: boolean = false;
  unsealBids: AgendaBidUnsealItem[] = [];
  isLoadingUnsealBids: boolean = false;
  unsealVotes: Map<string, UnsealVoteView[]> = new Map();
  downloadingUnsealDocuments: Set<string> = new Set();
  linkedAgendaEvaluations: any[] = [];
  isLoadingLinkedEvaluations: boolean = false;
  evaluationReviewItems: any[] = [];
  isLoadingEvaluationReviews: boolean = false;
  awardBids: any[] = [];
  isLoadingAwardBids: boolean = false;
  reviewingEvaluationId: string = '';
  agendaWorkflowActions = new Map<number, any[]>();

  openBidDocumentsDialog(bid: { bidId?: string; vendorName?: string; vendorId?: string }): void {
    if (!bid?.bidId) return;
    this.dialog.open(BidDocumentsDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: { bidId: bid.bidId, vendorName: bid.vendorName || bid.vendorId }
    });
  }
  
  // Voice recognition properties
  isRecording: boolean = false;
  isRecordingArabic: boolean = false; // Track if recording for Arabic field
  isVoiceSupported: boolean = false;
  currentVoiceTranscript: string = ''; // Current session's transcript
  currentVoiceTranscriptAr: string = ''; // Current session's transcript for Arabic
  hasNewVoiceText: boolean = false; // Flag for highlighting English field
  hasNewVoiceTextAr: boolean = false; // Flag for highlighting Arabic field
  private voiceRecognitionSubscriptions: Subscription[] = []; // Subscriptions for Azure Speech Service
  private currentRecordingField: 'en' | 'ar' | null = null; // Track which field is being recorded

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AdminMeetingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private dialog: MatDialog,
    private signalRService: SignalRService,
    private userService: UserService,
    private minimizedMeetingService: MinimizedMeetingService,
    private translationService: TranslationService,
    private toasterService: ToasterService,
    private azureSpeechService: AzureSpeechService
  ) {
    this.meetingTitle = data?.meetingTitle || '';
    this.meetingId = data?.meetingId || '';
    this.agendaItems = data?.agendaItems || [];
    
    // Sort agenda items by Order property (ascending - Item #1 first, Item #2 second, etc.)
    this.agendaItems.sort((a: any, b: any) => {
      const orderA = a.order || a.Order || 999;
      const orderB = b.order || b.Order || 999;
      return orderA - orderB;
    });
    
    this.currentAgendaIndex = data?.currentAgendaIndex || 0;
    
    (data?.agendaDecisions || []).forEach((decision: any) => {
      this.agendaDecisions.set(Number(decision.agendaIndex), {
        tcDecisionEn: decision.tcDecisionEn || '',
        tcDecisionAr: decision.tcDecisionAr || ''
      });
    });

    (data?.membersAnswers || []).forEach((agendaAnswers: any) => {
      this.membersAnswers.set(
        Number(agendaAnswers.agendaIndex),
        Array.isArray(agendaAnswers.answers) ? agendaAnswers.answers : []
      );
    });

    this.attendees = Array.isArray(data?.attendees) ? data.attendees : [];

    // Store meeting members with roles from data
    if (data?.meetingMembers && Array.isArray(data.meetingMembers)) {
      this.meetingMembers = data.meetingMembers.map((member: any) => ({
        userId: (member.userId || member.UserId || member.id || member.userIdInt || member.user?.id || member.user?.userId)?.toString() || '',
        userName: member.userName || member.UserName || member.name || '',
        role: member.role || member.Role || 'Member'
      }));
    }
    
    // Initialize form
    this.decisionForm = this.fb.group({
      tcDecisionEn: [''],
      tcDecisionAr: ['']
    });
  }

  ngOnInit(): void {
    console.log('Admin dialog ngOnInit:', {
      currentAgendaIndex: this.currentAgendaIndex,
      agendaItemsCount: this.agendaItems.length,
      agendaItems: this.agendaItems
    });

    // Initialize voice recognition
    this.initializeVoiceRecognition();

    const decisionEditorSyncSub = this.decisionForm.valueChanges.subscribe(() => {
      this.syncEditorViewsSoon();
    });
    this.subscriptions.push(decisionEditorSyncSub);

    // Initialize TC Decisions for all agenda items from API data (database)
    this.agendaItems.forEach((agendaItem, index) => {
      if (agendaItem.tcDecisionEN || agendaItem.tcDecisionAR) {
        // Only set if not already in agendaDecisions map
        if (!this.agendaDecisions.has(index)) {
          this.agendaDecisions.set(index, {
            tcDecisionEn: agendaItem.tcDecisionEN || '',
            tcDecisionAr: agendaItem.tcDecisionAR || ''
          });
        }
      }
    });

    // Load TC Decisions from SignalR session (will override database data if meeting is active)
    if (this.meetingId) {
      const allTCDecisionsSub = this.signalRService.allTCDecisions$.subscribe(
        (data) => {
          if (data.meetingId === this.meetingId && data.decisions) {
            console.log('Loaded TC Decisions from SignalR session:', data.decisions);
            // Override with session data
            Object.keys(data.decisions).forEach((key) => {
              const agendaIndex = parseInt(key);
              const decision = data.decisions[key];
              this.agendaDecisions.set(agendaIndex, {
                tcDecisionEn: decision.tcDecisionEn || '',
                tcDecisionAr: decision.tcDecisionAr || ''
              });
            });
            
            // Reload current agenda item's decision
            if (this.agendaDecisions.has(this.currentAgendaIndex)) {
              const savedDecision = this.agendaDecisions.get(this.currentAgendaIndex);
              this.decisionForm.patchValue({
                tcDecisionEn: savedDecision?.tcDecisionEn || '',
                tcDecisionAr: savedDecision?.tcDecisionAr || ''
              });
            }
          }
        }
      );
      this.subscriptions.push(allTCDecisionsSub);

      // Subscribe before requesting so a fast SignalR response cannot be missed.
      this.signalRService.getAllTCDecisions(this.meetingId);
    }

    // Load saved decision for current agenda item if exists
    if (this.agendaDecisions.has(this.currentAgendaIndex)) {
      const savedDecision = this.agendaDecisions.get(this.currentAgendaIndex);
      this.decisionForm.patchValue({
        tcDecisionEn: savedDecision?.tcDecisionEn || '',
        tcDecisionAr: savedDecision?.tcDecisionAr || ''
      });
    } else {
      // Auto-populate TC Decisions from agenda item data (from API response)
      this.autoPopulateTCDecisions(this.currentAgendaIndex);
    }

    // Get meeting attendees
    if (this.meetingId) {
      // Join meeting to get current agenda index from backend session
      const userId = this.userService.user?.Id?.toString() || '';
      const userName = this.userService.user?.FullName || '';
      this.signalRService.joinMeeting(this.meetingId, userId, userName);
      
      // Listen for meeting session state to get current agenda index from backend
      const sessionStateSub = this.signalRService.meetingSessionState$.subscribe(
        (state) => {
          if (state.meetingId === this.meetingId && state.currentAgendaIndex !== undefined) {
            console.log('MeetingSessionState received, updating current agenda index:', state.currentAgendaIndex);
            
            // Always update the current agenda index from backend session FIRST
            // This ensures we have the correct index before loading attachments
            this.currentAgendaIndex = state.currentAgendaIndex;
            
            // Scroll to the updated agenda item
            this.shouldScroll = true;
            
            // Reload members answers for the updated agenda index
            this.loadMembersAnswersForCurrentAgenda();
            
            // Load attachments AFTER the index has been updated
            // This ensures we load attachments for the correct agenda item (e.g., 5) not the initial one (0)
            this.loadAttachmentsForCurrentAgenda();
            this.loadUnsealBidsForCurrentAgenda();
            this.syncEvaluationAssignmentContext();
            
            // Load saved decision for the updated agenda index
            if (this.agendaDecisions.has(this.currentAgendaIndex)) {
              const savedDecision = this.agendaDecisions.get(this.currentAgendaIndex);
              this.decisionForm.patchValue({
                tcDecisionEn: savedDecision?.tcDecisionEn || '',
                tcDecisionAr: savedDecision?.tcDecisionAr || ''
              });
            } else {
              this.autoPopulateTCDecisions(this.currentAgendaIndex);
            }
          }
        }
      );
      this.subscriptions.push(sessionStateSub);
      
      this.signalRService.getMeetingAttendees(this.meetingId);
      
      // Listen for attendees updates
      const attendeesSub = this.signalRService.meetingAttendees$.subscribe(
        (attendees) => {
          this.attendees = attendees;
        }
      );
      this.subscriptions.push(attendeesSub);

      // Listen for meeting ended event
      const meetingEndedSub = this.signalRService.meetingEnded$.subscribe(
        (data) => {
          if (data.meetingId === this.meetingId) {
            console.log('Meeting ended, closing admin dialog');
            this.dialogRef.close({ action: 'ended' });
          }
        }
      );
      this.subscriptions.push(meetingEndedSub);

      // Listen for user joined/left events
      const userJoinedSub = this.signalRService.userJoined$.subscribe(
        (data) => {
          if (!this.attendees.find(a => a.userId === data.userId)) {
            this.attendees.push(data);
            this.signalRService.getMeetingAttendees(this.meetingId);
          }
        }
      );
      this.subscriptions.push(userJoinedSub);

      const userLeftSub = this.signalRService.userLeft$.subscribe(
        (data) => {
          this.attendees = this.attendees.filter(a => a.userId !== data.userId);
        }
      );
      this.subscriptions.push(userLeftSub);

      // Listen for user kicked event
      const userKickedSub = this.signalRService.userKicked$.subscribe(
        (data) => {
          if (data.meetingId === this.meetingId) {
            // Remove kicked user from attendees list
            this.attendees = this.attendees.filter(a => a.userId !== data.userId);
            // Also remove from members answers
            this.membersAnswers.forEach((answers, index) => {
              this.membersAnswers.set(index, answers.filter(a => a.userId !== data.userId));
            });
          }
        }
      );
      this.subscriptions.push(userKickedSub);

      // Listen for member answered agenda item
      const memberAnsweredSub = this.signalRService.memberAnsweredAgenda$.subscribe(
        (data) => {
          if (data.meetingId === this.meetingId) {
            this.updateMemberAnswer(data.agendaIndex, {
              userId: data.userId,
              userName: data.userName,
              action: data.action,
              comment: data.comment
            });
          }
        }
      );
      this.subscriptions.push(memberAnsweredSub);

      // Listen for all members answers
      const allMembersAnswersSub = this.signalRService.allMembersAnswers$.subscribe(
        (data) => {
          if (data.meetingId === this.meetingId) {
            this.membersAnswers.set(data.agendaIndex, data.answers);
          }
        }
      );
      this.subscriptions.push(allMembersAnswersSub);

      const memberUnsealVotedSub = this.signalRService.memberUnsealVoted$.subscribe((data) => {
        if (data.meetingId === this.meetingId && data.agendaIndex === this.currentAgendaIndex) {
          this.addOrUpdateUnsealVote(data);
        }
      });
      this.subscriptions.push(memberUnsealVotedSub);

      const allUnsealVotesSub = this.signalRService.allUnsealVotes$.subscribe((data) => {
        if (data.meetingId === this.meetingId && data.agendaIndex === this.currentAgendaIndex) {
          this.unsealVotes.clear();
          (data.votes || []).forEach(vote => this.addOrUpdateUnsealVote(vote));
        }
      });
      this.subscriptions.push(allUnsealVotesSub);

      const bidDocumentsUnsealedSub = this.signalRService.bidDocumentsUnsealed$.subscribe((data) => {
        if (data.meetingId === this.meetingId && data.agendaIndex === this.currentAgendaIndex) {
          this.loadUnsealBidsForCurrentAgenda();
        }
      });
      this.subscriptions.push(bidDocumentsUnsealedSub);

      const workflowActionSub = this.signalRService.agendaWorkflowActionUpdated$.subscribe((data) => {
        if (data.meetingId === this.meetingId) this.upsertAgendaWorkflowAction(data.agendaIndex, data.action);
      });
      this.subscriptions.push(workflowActionSub);

      const allWorkflowActionsSub = this.signalRService.allAgendaWorkflowActions$.subscribe((data) => {
        if (data.meetingId === this.meetingId) this.agendaWorkflowActions.set(data.agendaIndex, data.actions || []);
      });
      this.subscriptions.push(allWorkflowActionsSub);

      // Listen for members answers cleared for an agenda (after broadcast)
      const membersAnswersClearedSub = this.signalRService.membersAnswersClearedForAgenda$.subscribe(
        (data) => {
          if (data.meetingId === this.meetingId) {
            this.membersAnswers.set(data.agendaIndex, []);
          }
        }
      );
      this.subscriptions.push(membersAnswersClearedSub);

      // Request all members answers for current agenda item
      this.loadMembersAnswersForCurrentAgenda();
      this.loadUnsealBidsForCurrentAgenda();
      this.signalRService.getAgendaWorkflowActions(this.meetingId, this.currentAgendaIndex);
      this.syncEvaluationAssignmentContext();
      
      // Don't load attachments here - wait for session state to update currentAgendaIndex first
      // Attachments will be loaded when session state is received (see sessionStateSub below)
      
      // Scroll to current agenda item when dialog opens
      this.shouldScroll = true;
    }
    
    // Listen for agenda navigation events to scroll when navigating
    const agendaNavSub = this.signalRService.agendaNavigation$.subscribe(
      (navData) => {
        if (navData.meetingId === this.meetingId) {
          // Update current agenda index and scroll to it
          this.currentAgendaIndex = navData.agendaIndex;
          this.shouldScroll = true;
          // Reload members answers for the new agenda index
          this.loadMembersAnswersForCurrentAgenda();
          // Load attachments for the new agenda index
          this.loadAttachmentsForCurrentAgenda();
          this.loadUnsealBidsForCurrentAgenda();
          this.syncEvaluationAssignmentContext();
          // Load saved decision for the new agenda index
          if (this.agendaDecisions.has(this.currentAgendaIndex)) {
            const savedDecision = this.agendaDecisions.get(this.currentAgendaIndex);
            this.decisionForm.patchValue({
              tcDecisionEn: savedDecision?.tcDecisionEn || '',
              tcDecisionAr: savedDecision?.tcDecisionAr || ''
            });
          } else {
            this.autoPopulateTCDecisions(this.currentAgendaIndex);
          }
        }
      }
    );
    this.subscriptions.push(agendaNavSub);
  }

  // Broadcast the TC Decision while retaining member responses for the meeting recap.
  broadcastTCDecision(): void {
    if (!this.meetingId) return;

    const tcDecisionEn = this.decisionForm.get('tcDecisionEn')?.value || '';
    const tcDecisionAr = this.decisionForm.get('tcDecisionAr')?.value || '';

    console.log('[AdminDialog] Broadcasting TC Decision for meeting:', this.meetingId, 'agenda:', this.currentAgendaIndex);

    // Save to local map (session/memory only)
    this.agendaDecisions.set(this.currentAgendaIndex, {
      tcDecisionEn: tcDecisionEn,
      tcDecisionAr: tcDecisionAr
    });

    // Broadcast to all members via SignalR.
    this.signalRService.updateTCDecision(
      this.meetingId,
      this.currentAgendaIndex,
      tcDecisionEn,
      tcDecisionAr
    );
    console.log('[AdminDialog] Called signalRService.updateTCDecision');

    // Show success message
    this.toasterService.showSuccess(
      'TC Decision has been broadcasted to all attending members.',
      'Decision Broadcasted'
    );

    console.log('[AdminDialog] TC Decision broadcast complete');
  }

  // Update member answer for an agenda item
  private updateMemberAnswer(agendaIndex: number, answer: { userId: string; userName: string; action: string; comment: string }): void {
    if (!this.membersAnswers.has(agendaIndex)) {
      this.membersAnswers.set(agendaIndex, []);
    }
    
    const answers = this.membersAnswers.get(agendaIndex)!;
    const existingIndex = answers.findIndex(a => a.userId === answer.userId);
    
    if (existingIndex >= 0) {
      // Update existing answer
      answers[existingIndex] = answer;
    } else {
      // Add new answer
      answers.push(answer);
    }
  }

  // Load members answers for current agenda item
  private loadMembersAnswersForCurrentAgenda(): void {
    if (this.meetingId) {
      this.signalRService.getAllMembersAnswers(this.meetingId, this.currentAgendaIndex);
    }
  }

  // Get members answers for current agenda item
  get currentAgendaMembersAnswers(): Array<{ userId: string; userName: string; action: string; comment: string }> {
    return this.membersAnswers.get(this.currentAgendaIndex) || [];
  }

  // Get count of accepted answers for current agenda
  get acceptedCount(): number {
    return this.currentAgendaMembersAnswers.filter(a => a.action === 'accept').length;
  }

  // Get count of rejected answers for current agenda
  get rejectedCount(): number {
    return this.currentAgendaMembersAnswers.filter(a => a.action === 'reject').length;
  }

  // Get count of deferred answers for current agenda
  get deferredCount(): number {
    return this.currentAgendaMembersAnswers.filter(a => a.action === 'defer').length;
  }

  // Get current user ID (hoster)
  get currentUserId(): string {
    return this.userService.user?.Id?.toString() || '';
  }

  // Get role for an attendee by matching with meetingMembers
  getAttendeeRole(userId: string): string {
    const member = this.meetingMembers.find(m => m.userId?.toString() === userId?.toString());
    return member?.role || 'Member';
  }

  // Format role name for display (e.g., "ViceChairman" -> "Vice Chairman")
  formatRole(role: string): string {
    if (!role) return 'Member';
    
    // Handle common role variations
    const roleMap: { [key: string]: string } = {
      'Chairman': 'Chairman',
      'CHAIRMAN': 'Chairman',
      'ViceChairman': 'Vice Chairman',
      'VICECHAIRMAN': 'Vice Chairman',
      'Vice Chairman': 'Vice Chairman',
      'VICE CHAIRMAN': 'Vice Chairman',
      'Secretary': 'Secretary',
      'SECRETARY': 'Secretary',
      'Tender Committee Secretary': 'Secretary',
      'TCM': 'Member',
      'tcm': 'Member',
      'Observer': 'Observer',
      'OBSERVER': 'Observer',
      'Member': 'Member',
      'MEMBER': 'Member'
    };
    
    return roleMap[role] || role;
  }

  // Get voting members (TCM, Chairman, Vice Chairman) - exclude hoster and Observers
  get tcmMembers(): Array<{ userId: string; userName: string }> {
    const currentUserId = this.currentUserId;
    return this.attendees.filter(attendee => {
      if (attendee.userId === currentUserId) {
        return false; // Exclude hoster
      }
      
      // Get role from meetingMembers
      const member = this.meetingMembers.find(m => m.userId?.toString() === attendee.userId?.toString());
      const role = (member?.role || '').toUpperCase().replace(/\s+/g, '');
      
      // Exclude Observers - only include voting members (TCM, Chairman, Vice Chairman)
      // Observers should not be required to answer
      return role !== 'OBSERVER';
    });
  }

  // Get Chairman and Vice Chairman members (excluding hoster)
  get chairmanAndViceChairmanMembers(): Array<{ userId: string; userName: string; role: string }> {
    const currentUserId = this.currentUserId;
    return this.meetingMembers.filter(member => {
      const userId = member.userId?.toString();
      if (userId === currentUserId) {
        return false; // Exclude hoster
      }
      const role = (member.role || '').toUpperCase().replace(/\s+/g, '');
      return role === 'CHAIRMAN' || role === 'VICECHAIRMAN';
    });
  }

  // Check if at least one Chairman or Vice Chairman has answered
  get hasChairmanOrViceChairmanAnswered(): boolean {
    const chairmanViceMembers = this.chairmanAndViceChairmanMembers;
    
    if (chairmanViceMembers.length === 0) {
      return true; // No Chairman/Vice Chairman, allow navigation
    }
    
    const currentUserId = this.currentUserId;
    const chairmanViceUserIds = chairmanViceMembers.map(m => m.userId?.toString()).filter(id => id);
    
    // Check if at least one Chairman or Vice Chairman has answered
    const chairmanViceAnswers = this.currentAgendaMembersAnswers.filter(
      answer => {
        const answerUserId = answer.userId?.toString();
        return answerUserId !== currentUserId && chairmanViceUserIds.includes(answerUserId);
      }
    );
    
    return chairmanViceAnswers.length > 0;
  }

  // Check if all voting members (excluding hoster and Observers) have answered the current agenda item
  get allMembersAnswered(): boolean {
    const votingMembers = this.tcmMembers; // Already excludes Observers and hoster
    
    if (votingMembers.length === 0) {
      return true; // No voting members, allow navigation
    }
    
    // First check if at least one Chairman or Vice Chairman has answered
    if (!this.hasChairmanOrViceChairmanAnswered) {
      return false;
    }
    
    const currentUserId = this.currentUserId;
    
    // Filter out hoster's answers and Observer answers - only count voting member answers
    const votingAnswers = this.currentAgendaMembersAnswers.filter(answer => {
      if (answer.userId === currentUserId) {
        return false; // Exclude hoster
      }
      
      // Check if this answer is from an Observer
      const member = this.meetingMembers.find(m => m.userId?.toString() === answer.userId?.toString());
      const role = (member?.role || '').toUpperCase().replace(/\s+/g, '');
      
      // Exclude Observer answers
      return role !== 'OBSERVER';
    });
    
    // All voting members (TCM, Chairman, Vice Chairman) should have answered (accept, reject, or defer)
    return votingAnswers.length >= votingMembers.length;
  }

  // Get count of voting members who haven't answered (excluding hoster and Observers)
  get unansweredCount(): number {
    const votingMembers = this.tcmMembers; // Already excludes Observers and hoster
    const currentUserId = this.currentUserId;
    
    // Filter out hoster's answers and Observer answers - only count voting member answers
    const votingAnswers = this.currentAgendaMembersAnswers.filter(answer => {
      if (answer.userId === currentUserId) {
        return false; // Exclude hoster
      }
      
      // Check if this answer is from an Observer
      const member = this.meetingMembers.find(m => m.userId?.toString() === answer.userId?.toString());
      const role = (member?.role || '').toUpperCase().replace(/\s+/g, '');
      
      // Exclude Observer answers
      return role !== 'OBSERVER';
    });
    
    return Math.max(0, votingMembers.length - votingAnswers.length);
  }


  get currentUnsealScope(): 'technical' | 'commercial' | 'combined' | null {
    const stage = (this.currentAgendaItem?.currentStage || this.currentAgendaItem?.CurrentStage || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!stage.includes('unsealing')) {
      return null;
    }
    if (stage.includes('combined')) {
      return 'combined';
    }
    if (stage.includes('technical')) {
      return 'technical';
    }
    if (stage.includes('commercial')) {
      return 'commercial';
    }
    return null;
  }

  get shouldShowUnsealSection(): boolean {
    return !!this.currentUnsealScope;
  }

  get isEvaluationReviewStage(): boolean {
    return this.getNormalizedCurrentAgendaStage() === 'evaluationreview';
  }

  get isAwardBidStage(): boolean {
    return this.getNormalizedCurrentAgendaStage() === 'awardbid';
  }

  private getNormalizedCurrentAgendaStage(): string {
    return (this.currentAgendaItem?.currentStage || this.currentAgendaItem?.CurrentStage || '')
      .toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  get unsealSectionTitle(): string {
    switch (this.currentUnsealScope) {
      case 'technical': return 'Unsealing Technical Documents';
      case 'commercial': return 'Unsealing Commercial Documents';
      case 'combined': return 'Unsealing Combined Documents';
      default: return 'Bid Document Unsealing';
    }

    this.syncEvaluationAssignmentContext();
  }

  getAgendaStageClass(stage: any): string {
    const value = (stage || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (value.includes('unsealingtechnical')) return 'stage-unseal-technical';
    if (value.includes('unsealingcommercial')) return 'stage-unseal-commercial';
    if (value.includes('unsealingcombined')) return 'stage-unseal-combined';
    if (value.includes('evaluationreview')) return 'stage-evaluation-review';
    if (value.includes('awardbid')) return 'stage-award-bid';
    return '';
  }

  get currentAgendaPrNumber(): string {
    const agendaItem = this.currentAgendaItem || {};
    const prIdentifiers = agendaItem.prIdentifiers || agendaItem.PRIdentifiers || agendaItem.prs || agendaItem.PRs;
    if (Array.isArray(prIdentifiers) && prIdentifiers.length > 0) {
      const firstPr = prIdentifiers[0];
      if (typeof firstPr === 'string' || typeof firstPr === 'number') {
        return firstPr.toString().trim();
      }
      return (firstPr.prNumber || firstPr.PRNumber || firstPr.value || firstPr.id || '').toString().trim();
    }

    return (
      agendaItem.linkedPR ||
      agendaItem.LinkedPR ||
      agendaItem.prNumber ||
      agendaItem.PRNumber ||
      agendaItem.pr ||
      agendaItem.PR ||
      ''
    ).toString().trim();
  }

  get shouldShowEvaluatorAssignmentSection(): boolean {
    return this.shouldShowUnsealSection || this.isEvaluationReviewStage;
  }

  private getDefaultEvaluationTypeForStage(): string {
    if (this.isEvaluationReviewStage) return 'Negotiation';
    if (this.currentUnsealScope === 'technical') return 'Technical';
    if (this.currentUnsealScope === 'commercial') return 'Commercial';
    if (this.currentUnsealScope === 'combined') return 'Combined';
    return 'Technical';
  }

  private async loadLinkedEvaluationsForCurrentAgenda(): Promise<void> {
    const prNumber = this.currentAgendaPrNumber;
    if (!prNumber || !this.shouldShowEvaluatorAssignmentSection) {
      this.linkedAgendaEvaluations = [];
      return;
    }

    this.isLoadingLinkedEvaluations = true;
    try {
      const url = `${environment.API_URL}/api/Evaluations/GetAllEvaluations`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const evaluations = response?.data ?? response?.Data ?? [];
      const normalizedPr = prNumber.toString().trim().toLowerCase();
      const allowedEvaluationTypes = this.isEvaluationReviewStage
        ? ['negotiation']
        : this.currentUnsealScope === 'technical'
        ? ['technical']
        : this.currentUnsealScope === 'commercial'
          ? ['commercial']
          : this.currentUnsealScope === 'combined'
            ? ['combined']
            : ['technical', 'commercial'];
      this.linkedAgendaEvaluations = Array.isArray(evaluations)
        ? evaluations.filter((evaluation: any) => {
          const evaluationPr = (evaluation.prNumber || evaluation.PrNumber || '').toString().trim().toLowerCase();
          const evaluationType = (evaluation.evaluationType || evaluation.EvaluationType || '').toString().trim().toLowerCase();
          return evaluationPr === normalizedPr && allowedEvaluationTypes.includes(evaluationType);
        })
        : [];
    } catch (error) {
      console.error('Failed to load linked evaluations for agenda:', error);
      this.linkedAgendaEvaluations = [];
    } finally {
      this.isLoadingLinkedEvaluations = false;
    }
  }

  private syncEvaluationAssignmentContext(): void {
    if (!this.shouldShowEvaluatorAssignmentSection) {
      this.linkedAgendaEvaluations = [];
      return;
    }

    this.loadLinkedEvaluationsForCurrentAgenda();
  }

  getAssignedEvaluatorNames(evaluation: any): string {
    const assigned = evaluation.assignedEvaluators || evaluation.AssignedEvaluators || [];
    if (Array.isArray(assigned) && assigned.length > 0) {
      return assigned
        .map((evaluator: any) => evaluator.fullName || evaluator.FullName || evaluator.email || evaluator.Email)
        .filter(Boolean)
        .join(', ');
    }

    return evaluation.assignedToEn || evaluation.AssignedToEn || '-';
  }

  openAssignEvaluatorsFromMeeting(): void {
    if (!this.currentAgendaPrNumber) {
      this.toasterService.showError('Linked PR is required before assigning evaluators.', 'Validation Error');
      return;
    }

    const dialogRef = this.dialog.open(AssignEvaluatorsComponent, {
      width: '1100px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      panelClass: 'assign-evaluators-dialog',
      data: {
        prNumber: this.currentAgendaPrNumber,
        pr: this.currentAgendaPrNumber,
        evaluationType: this.getDefaultEvaluationTypeForStage(),
        lockEvaluationType: this.isEvaluationReviewStage
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.action === 'assigned') {
        this.loadLinkedEvaluationsForCurrentAgenda();
      }
    });
  }

  private getCurrentAgendaItemId(): number {
    return Number(this.currentAgendaItem?.id || this.currentAgendaItem?.ID || this.currentAgendaItem?.agendaItemId || this.currentAgendaItem?.AgendaItemId || 0);
  }

  async loadUnsealBidsForCurrentAgenda(): Promise<void> {
    void this.loadEvaluationReviewsForCurrentAgenda();
    void this.loadAwardBidsForCurrentAgenda();
    if (!this.shouldShowBidContext) {
      this.unsealBids = [];
      this.unsealVotes.clear();
      return;
    }

    const agendaItemId = this.getCurrentAgendaItemId();
    if (!agendaItemId) {
      this.unsealBids = [];
      return;
    }

    this.isLoadingUnsealBids = true;
    try {
      const url = `${environment.API_URL}/api/Meetings/agenda/${agendaItemId}/unseal-bids`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      this.unsealBids = response?.data || response?.Data || [];
      if (this.meetingId) {
        await this.signalRService.getAllUnsealVotes(this.meetingId, this.currentAgendaIndex);
      }
    } catch (error) {
      console.error('Failed to load agenda bids for unsealing:', error);
      this.unsealBids = [];
    } finally {
      this.isLoadingUnsealBids = false;
    }
  }

  async loadEvaluationReviewsForCurrentAgenda(): Promise<void> {
    if (!this.shouldShowBidContext) {
      this.evaluationReviewItems = [];
      return;
    }
    const agendaItemId = this.getCurrentAgendaItemId();
    if (!agendaItemId) return;
    this.isLoadingEvaluationReviews = true;
    try {
      const response: any = await lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/Meetings/agenda/${agendaItemId}/evaluation-reviews`));
      this.evaluationReviewItems = response?.data || response?.Data || [];
    } catch (error) {
      console.error('Failed to load evaluation reviews:', error);
      this.evaluationReviewItems = [];
    } finally {
      this.isLoadingEvaluationReviews = false;
    }
  }

  canReviewAgendaEvaluation(evaluation: any): boolean {
    const evaluationId = evaluation?.evaluationId || evaluation?.EvaluationId;
    return this.isCurrentUserSecretary()
      && (evaluation?.status || evaluation?.Status || '').toString().toLowerCase() === 'evaluated'
      && !this.getAgendaWorkflowAction('evaluation-review', evaluationId);
  }

  isCurrentUserSecretary(): boolean {
    const role = (this.userService.user?.Role || '').toString().toLowerCase();
    return role === 'secretary' || role === 'tender committee secretary' || role === '4';
  }

  async reviewAgendaEvaluation(evaluation: any, isApproved: boolean): Promise<void> {
    if (!this.canReviewAgendaEvaluation(evaluation) || this.reviewingEvaluationId) return;
    if (isApproved && !(await this.confirmSecretaryAction(
      'Accept Evaluation',
      'Are you sure you want to accept this evaluation? This decision will be saved when the meeting is finished.',
      'Accept'))) {
      return;
    }

    const comments = isApproved ? '' : window.prompt('Enter change request comments:')?.trim();
    if (!isApproved && !comments) return;
    this.reviewingEvaluationId = evaluation.evaluationId || evaluation.EvaluationId;
    try {
      await this.signalRService.saveAgendaWorkflowAction(this.meetingId, this.currentAgendaIndex, 'evaluation-review', this.reviewingEvaluationId, isApproved, comments);
      this.upsertAgendaWorkflowAction(this.currentAgendaIndex, { actionType: 'evaluation-review', entityId: this.reviewingEvaluationId, isApproved, comments });
      this.toasterService.showSuccess(isApproved ? 'Evaluation acceptance will be saved when the meeting is finished.' : 'Change request will be saved when the meeting is finished.', 'Saved in meeting');
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || 'Unable to review evaluation.', 'Error');
    } finally {
      this.reviewingEvaluationId = '';
    }
  }

  async downloadEvaluationReport(report: any): Promise<void> {
    if (!report?.attachmentId) return;
    await this.downloadAttachment({
      name: report.name || 'Evaluation Report',
      url: `${environment.API_URL}/api/Documents/DownloadDocument/${report.attachmentId}`,
      fileType: report.mimeType || 'application/octet-stream'
    });
  }

  async loadAwardBidsForCurrentAgenda(): Promise<void> {
    if (!this.isAwardBidStage) {
      this.awardBids = [];
      return;
    }
    const agendaItemId = this.getCurrentAgendaItemId();
    if (!agendaItemId) return;
    this.isLoadingAwardBids = true;
    try {
      const response: any = await lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/Meetings/agenda/${agendaItemId}/award-bids`));
      this.awardBids = response?.data || response?.Data || [];
    } catch (error) {
      console.error('Failed to load award bids:', error);
      this.awardBids = [];
    } finally {
      this.isLoadingAwardBids = false;
    }
  }

  async selectCommitteeWinner(bid: any): Promise<void> {
    if (!bid?.bidId) return;
    const confirmed = await this.confirmSecretaryAction(
      'Select Winner',
      `Are you sure you want to select ${bid.bidId} as the winning bid? This decision will be saved when the meeting is finished.`,
      'Select Winner');
    if (!confirmed) return;

    try {
      await this.signalRService.saveAgendaWorkflowAction(this.meetingId, this.currentAgendaIndex, 'award', bid.bidId);
      this.upsertAgendaWorkflowAction(this.currentAgendaIndex, { actionType: 'award', entityId: bid.bidId });
      this.toasterService.showSuccess('Winner selection will be saved when the meeting is finished.', 'Saved in meeting');
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || 'Unable to record winner.', 'Error');
    }
  }

  private async confirmSecretaryAction(title: string, message: string, confirmText: string): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title,
        message,
        confirmText,
        cancelText: 'Cancel'
      }
    });

    return !!await lastValueFrom(dialogRef.afterClosed());
  }

  get shouldShowBidContext(): boolean {
    return this.shouldShowUnsealSection || this.isEvaluationReviewStage || this.isAwardBidStage;
  }

  getAgendaWorkflowAction(actionType: string, entityId: string): any {
    return (this.agendaWorkflowActions.get(this.currentAgendaIndex) || []).find(action => action.actionType === actionType && action.entityId === entityId);
  }

  getEvaluationReviewActionText(evaluation: any): string {
    const action = this.getAgendaWorkflowAction('evaluation-review', evaluation.evaluationId || evaluation.EvaluationId);
    return action ? (action.isApproved ? 'Accepted' : 'Change Request') : (evaluation.reviewAction || evaluation.ReviewAction || '-');
  }

  getEvaluationReviewComments(evaluation: any): string {
    const action = this.getAgendaWorkflowAction('evaluation-review', evaluation.evaluationId || evaluation.EvaluationId);
    return action?.comments || evaluation.reviewComments || evaluation.ReviewComments || '-';
  }

  hasSecretaryChangeRequest(): boolean {
    return this.evaluationReviewItems.some(evaluation => this.getEvaluationReviewActionText(evaluation) === 'Change Request');
  }

  showEvaluatorComments(evaluation: any): void {
    this.dialog.open(MessageDialogComponent, {
      width: '500px',
      data: {
        title: 'Evaluator Comments',
        message: evaluation?.comments || evaluation?.Comments || 'No evaluator comments were provided.',
        type: 'info',
        buttonText: 'Close'
      }
    });
  }

  getCommitteeBidStatus(bid: any): string {
    const selected = (this.agendaWorkflowActions.get(this.currentAgendaIndex) || []).find(action => action.actionType === 'award');
    return selected ? (selected.entityId === bid.bidId ? 'Awarded' : 'Regret') : (bid.tCComitteeStatus || bid.TCComitteeStatus || 'InProgress');
  }

  private upsertAgendaWorkflowAction(agendaIndex: number, action: any): void {
    const actions = (this.agendaWorkflowActions.get(agendaIndex) || []).filter(existing => {
      if (action.actionType === 'award' && existing.actionType === 'award') {
        return false;
      }

      return !(existing.actionType === action.actionType && existing.entityId === action.entityId);
    });
    actions.push(action);
    this.agendaWorkflowActions.set(agendaIndex, actions);
  }

  addOrUpdateUnsealVote(vote: any): void {
    if (!vote?.bidId || !vote?.userId) {
      return;
    }

    const currentVotes = this.unsealVotes.get(vote.bidId) || [];
    const updatedVotes = currentVotes.filter(v => v.userId?.toString() !== vote.userId?.toString());
    updatedVotes.push({
      bidId: vote.bidId,
      userId: vote.userId,
      userName: vote.userName,
      documentScope: vote.documentScope,
      votedAt: vote.votedAt
    });
    this.unsealVotes.set(vote.bidId, updatedVotes);
  }

  getRequiredUnsealMembers(): Array<{ userId: string; userName: string; role: string }> {
    const blockedRoles = ['secretary', 'observer'];
    const attendeesByUserId = new Map(
      (this.attendees || [])
        .filter(attendee => !!attendee?.userId)
        .map(attendee => [attendee.userId.toString(), attendee])
    );

    // Unsealing requires confirmation only from people who actually joined this meeting.
    // meetingMembers contains everyone initially invited, including members who are absent.
    return (this.meetingMembers || [])
      .filter(member => !!member.userId && attendeesByUserId.has(member.userId.toString()))
      .filter(member => !blockedRoles.includes((member.role || '').toLowerCase().replace(/\s/g, '')))
      .map(member => ({
        ...member,
        userName: attendeesByUserId.get(member.userId.toString())?.userName || member.userName
      }));
  }

  getUnsealVoteCount(bid: AgendaBidUnsealItem): number {
    if (this.isBidUnsealedForScope(bid)) {
      return this.getUnsealRequiredCount();
    }

    return (this.unsealVotes.get(bid.bidId) || []).length;
  }

  getUnsealRequiredCount(): number {
    return this.getRequiredUnsealMembers().length;
  }

  getUnsealConfirmationText(bid: AgendaBidUnsealItem): string {
    if (this.isBidUnsealedForScope(bid)) {
      return 'Unsealed';
    }

    return `${this.getUnsealVoteCount(bid)} / ${this.getUnsealRequiredCount()} confirmed`;
  }

  getUnsealMemberStatuses(bid: AgendaBidUnsealItem): Array<{ userId: string; userName: string; role: string; hasConfirmed: boolean; votedAt?: string }> {
    const votes = this.unsealVotes.get(bid.bidId) || [];
    const voteMap = new Map(votes.map(vote => [vote.userId?.toString(), vote]));
    const isUnsealed = this.isBidUnsealedForScope(bid);

    return this.getRequiredUnsealMembers().map(member => {
      const vote = voteMap.get(member.userId?.toString());
      return {
        userId: member.userId,
        userName: member.userName,
        role: member.role,
        hasConfirmed: isUnsealed || !!vote,
        votedAt: vote?.votedAt
      };
    });
  }

  getUnsealMemberStateText(bid: AgendaBidUnsealItem, memberStatus: { hasConfirmed: boolean }): string {
    if (this.isBidUnsealedForScope(bid)) {
      return memberStatus.hasConfirmed ? 'Unsealed' : 'Pending';
    }

    return memberStatus.hasConfirmed ? 'Confirmed' : 'Pending';
  }

  canSecretaryUnsealBid(bid: AgendaBidUnsealItem): boolean {
    if (this.isCommercialUnsealBlockedByTechnicalRejection(bid)) {
      return !this.isBidUnsealedForScope(bid) && this.hasBidDocumentForScope(bid);
    }

    const requiredMembers = this.getRequiredUnsealMembers();
    if (!requiredMembers.length || this.isBidUnsealedForScope(bid)) {
      return false;
    }

    const votedUserIds = new Set((this.unsealVotes.get(bid.bidId) || []).map(vote => vote.userId?.toString()));
    return requiredMembers.every(member => votedUserIds.has(member.userId?.toString()));
  }

  hasBidDocumentForScope(bid: AgendaBidUnsealItem): boolean {
    if (this.currentUnsealScope === 'technical') return bid.hasTechnicalDocument;
    if (this.currentUnsealScope === 'commercial') return bid.hasCommercialDocument;
    return bid.hasTechnicalDocument || bid.hasCommercialDocument;
  }

  isBidUnsealedForScope(bid: AgendaBidUnsealItem): boolean {
    if (this.currentUnsealScope === 'technical') return bid.hasTechnicalDocument && bid.technicalUnsealed;
    if (this.currentUnsealScope === 'commercial') return bid.hasCommercialDocument && bid.commercialUnsealed;
    return (!bid.hasTechnicalDocument || bid.technicalUnsealed) && (!bid.hasCommercialDocument || bid.commercialUnsealed);
  }

  getScopeStatusText(bid: AgendaBidUnsealItem): string {
    if (!this.hasBidDocumentForScope(bid)) return 'No matching documents';
    return this.isBidUnsealedForScope(bid) ? 'Unsealed' : 'Sealed';
  }

  get currentUnsealPendingCount(): number {
    if (!this.shouldShowUnsealSection) {
      return 0;
    }

    return (this.unsealBids || [])
      .filter(bid => this.hasBidDocumentForScope(bid))
      .filter(bid => !this.isCommercialUnsealBlockedByTechnicalRejection(bid))
      .filter(bid => !this.isBidUnsealedForScope(bid))
      .length;
  }

  get isCurrentUnsealAgendaComplete(): boolean {
    if (!this.shouldShowUnsealSection) {
      return true;
    }

    return !this.isLoadingUnsealBids && this.currentUnsealPendingCount === 0;
  }

  get canMoveForwardFromCurrentAgenda(): boolean {
    return this.allMembersAnswered && this.isCurrentUnsealAgendaComplete;
  }

  getForwardNavigationBlockedMessage(targetLabel: string): string {
    if (!this.allMembersAnswered) {
      if (!this.hasChairmanOrViceChairmanAnswered) {
        return `Cannot ${targetLabel}. At least one Chairman or Vice Chairman must provide their answer before proceeding.`;
      }

      return `Cannot ${targetLabel}. ${this.unansweredCount} committee member(s) have not provided their answer yet. Please wait for all voting members (Chairman, Vice Chairman, TCM) to accept or reject the current agenda item before proceeding.`;
    }

    if (!this.isCurrentUnsealAgendaComplete) {
      if (this.isLoadingUnsealBids) {
        return `Cannot ${targetLabel}. Related bid documents are still loading. Please wait and try again.`;
      }

      return `Cannot ${targetLabel}. ${this.currentUnsealPendingCount} related bid(s) still have sealed documents for this unsealing stage.`;
    }

    return '';
  }

  private showForwardNavigationBlockedDialog(title: string, targetLabel: string): void {
    this.dialog.open(MessageDialogComponent, {
      width: '450px',
      data: {
        title,
        message: this.getForwardNavigationBlockedMessage(targetLabel),
        type: 'warning'
      }
    });
  }


  getVisibleUnsealDocuments(bid: AgendaBidUnsealItem): AgendaBidUnsealDocument[] {
    const documents: AgendaBidUnsealDocument[] = [];
    if (this.currentUnsealScope === 'technical' || this.currentUnsealScope === 'combined') {
      documents.push(...((bid.technicalDocuments || []).filter(document => document.isUnsealed)));
    }
    if (this.currentUnsealScope === 'commercial' || this.currentUnsealScope === 'combined') {
      documents.push(...((bid.commercialDocuments || []).filter(document => document.isUnsealed)));
    }
    return documents;
  }

  getUnsealDocumentKey(document: AgendaBidUnsealDocument): string {
    return document?.attachmentId || document?.filePath || document?.name || '';
  }

  isUnsealDocumentDownloading(document: AgendaBidUnsealDocument): boolean {
    const key = this.getUnsealDocumentKey(document);
    return !!key && this.downloadingUnsealDocuments.has(key);
  }

  async openUnsealDocument(document: AgendaBidUnsealDocument): Promise<void> {
    const key = this.getUnsealDocumentKey(document);
    if (document?.attachmentId && key) {
      this.downloadingUnsealDocuments.add(key);
      try {
        await this.downloadAttachment({
          name: document.name || document.attachmentFor || 'Document',
          url: `${environment.API_URL}/api/Documents/DownloadDocument/${document.attachmentId}`,
          fileType: document.mimeType || 'application/octet-stream'
        });
      } finally {
        this.downloadingUnsealDocuments.delete(key);
      }
      return;
    }

    this.toasterService.showError('Document attachment ID is not available.', 'Document Error');
  }

  formatUnsealFileSize(value: any): string {
    const size = Number(value || 0);
    if (!size) return '';
    if (size >= 1024) return `${(size / 1024).toFixed(2)} MB`;
    return `${size.toFixed(2)} KB`;
  }

  getUnsealDocumentSummary(bid: AgendaBidUnsealItem): string {
    if (this.currentUnsealScope === 'technical') return bid.hasTechnicalDocument ? 'Technical proposal' : 'No technical proposal';
    if (this.currentUnsealScope === 'commercial') return bid.hasCommercialDocument ? 'Commercial proposal' : 'No commercial proposal';
    const docs: string[] = [];
    docs.push(bid.hasTechnicalDocument ? 'Technical proposal' : 'No technical proposal');
    docs.push(bid.hasCommercialDocument ? 'Commercial proposal' : 'No commercial proposal');
    return docs.join(' | ');
  }

  isCommercialUnsealBlockedByTechnicalRejection(bid: AgendaBidUnsealItem): boolean {
    if (this.currentUnsealScope !== 'commercial' && this.currentUnsealScope !== 'combined') {
      return false;
    }

    const technicalDecision = (bid.isTechnicallyRejected
      || (bid.technicalEvaluationDecision || '').toLowerCase().includes('reject')
      || (bid.evaluations || []).some((evaluation: any) =>
        (evaluation.evaluationType || '').toLowerCase() === 'technical'
        && (evaluation.decision || '').toLowerCase().includes('reject')));

    return technicalDecision;
  }

  getSecretaryUnsealButtonText(bid: AgendaBidUnsealItem): string {
    return this.isCommercialUnsealBlockedByTechnicalRejection(bid) ? 'Override & Unseal' : 'Unseal';
  }

  async secretaryUnsealBid(bid: AgendaBidUnsealItem): Promise<void> {
    if (!this.canSecretaryUnsealBid(bid)) {
      return;
    }

    const agendaItemId = this.getCurrentAgendaItemId();
    const payload = {
      meetingId: this.meetingId,
      agendaIndex: this.currentAgendaIndex,
      agendaItemId,
      bidId: bid.bidId,
      documentScope: this.currentUnsealScope,
      overrideTechnicalRejection: this.isCommercialUnsealBlockedByTechnicalRejection(bid)
    };

    try {
      const url = `${environment.API_URL}/api/Meetings/unseal-bid-documents`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));
      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bid documents unsealed successfully.', 'Success');
        await this.loadUnsealBidsForCurrentAgenda();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to unseal bid documents.', 'Error');
      }
    } catch (error) {
      console.error('Failed to unseal bid documents:', error);
      this.toasterService.showError('Failed to unseal bid documents.', 'Error');
    }
  }

  formatBidDate(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
  }

  formatBidAmount(value: any): string {
    const amount = Number(value);
    return isNaN(amount) ? '-' : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Unsubscribe from voice recognition subscriptions
    this.voiceRecognitionSubscriptions.forEach(sub => sub.unsubscribe());
    this.voiceRecognitionSubscriptions = [];
    
    // Stop voice recognition if active
    if (this.isRecording || this.isRecordingArabic) {
      this.azureSpeechService.stopRecognition();
    }
  }

  ngAfterViewInit(): void {
    this.syncEditorViews();
  }

  // Initialize voice recognition
  private initializeVoiceRecognition(): void {
    // Check if Azure Speech Service is available
    if (!this.azureSpeechService.isAvailable()) {
      console.warn('Azure Speech Service is not available. Please check configuration.');
      this.isVoiceSupported = false;
      return;
    }

    this.isVoiceSupported = true;

    // Subscribe to recognition results
    const recognitionResultSub = this.azureSpeechService.recognitionResult$.subscribe(
      (result) => {
        if (this.currentRecordingField === 'en') {
          // English field recording
          if (result.isFinal) {
            // Final result - update the English textarea
            const currentValue = this.decisionForm.get('tcDecisionEn')?.value || '';
            const newValue = this.appendPlainTextToEditorHtml(currentValue, result.text.trim());
            
            this.decisionForm.patchValue({ tcDecisionEn: newValue });
            this.syncEditorViews();
            
            // Update the current session transcript
            this.currentVoiceTranscript = result.text.trim();
            this.hasNewVoiceText = true;
            
            // Clear the highlight after 3 seconds
            setTimeout(() => {
              this.hasNewVoiceText = false;
            }, 3000);
          } else {
            // Interim result - show in indicator
            this.currentVoiceTranscript = result.text;
          }
        } else if (this.currentRecordingField === 'ar') {
          // Arabic field recording
          if (result.isFinal) {
            // Final result - update the Arabic textarea
            const currentValue = this.decisionForm.get('tcDecisionAr')?.value || '';
            const newValue = this.appendPlainTextToEditorHtml(currentValue, result.text.trim());
            
            this.decisionForm.patchValue({ tcDecisionAr: newValue });
            this.syncEditorViews();
            
            // Update the current session transcript
            this.currentVoiceTranscriptAr = result.text.trim();
            this.hasNewVoiceTextAr = true;
            
            // Clear the highlight after 3 seconds
            setTimeout(() => {
              this.hasNewVoiceTextAr = false;
            }, 3000);
          } else {
            // Interim result - show in indicator
            this.currentVoiceTranscriptAr = result.text;
          }
        }
      }
    );
    this.voiceRecognitionSubscriptions.push(recognitionResultSub);

    // Subscribe to recognition errors
    const recognitionErrorSub = this.azureSpeechService.recognitionError$.subscribe(
      (error) => {
        console.error('Azure Speech recognition error:', error);
        this.isRecording = false;
        this.isRecordingArabic = false;
        this.currentVoiceTranscript = '';
        this.currentVoiceTranscriptAr = '';
        this.currentRecordingField = null;
        
        if (error.includes('microphone') || error.includes('permission')) {
          this.toasterService.showError('Microphone access denied. Please allow microphone access in your browser.', 'Voice Input Error');
        } else if (error.includes('No speech')) {
          this.toasterService.showWarning('No speech detected. Please try again.', 'No Speech');
        } else {
          this.toasterService.showError('Voice recognition error: ' + error, 'Voice Input Error');
        }
      }
    );
    this.voiceRecognitionSubscriptions.push(recognitionErrorSub);

    // Subscribe to recognition ended
    const recognitionEndedSub = this.azureSpeechService.recognitionEnded$.subscribe(
      () => {
        this.isRecording = false;
        this.isRecordingArabic = false;
        this.currentRecordingField = null;
        // Clear the transcript indicators after a short delay
        setTimeout(() => {
          this.currentVoiceTranscript = '';
          this.currentVoiceTranscriptAr = '';
        }, 2000);
      }
    );
    this.voiceRecognitionSubscriptions.push(recognitionEndedSub);
  }

  // Toggle voice input for English
  async toggleVoiceInput(): Promise<void> {
    if (!this.isVoiceSupported || !this.azureSpeechService.isAvailable()) {
      this.toasterService.showError('Azure Speech Service is not available. Please check configuration.', 'Not Supported');
      return;
    }

    // If recording for Arabic, stop it first
    if (this.isRecordingArabic) {
      await this.azureSpeechService.stopRecognition();
      this.isRecordingArabic = false;
    }

    if (this.isRecording) {
      // Stop recording
      await this.azureSpeechService.stopRecognition();
      this.isRecording = false;
      this.currentRecordingField = null;
      this.toasterService.showInfo('Voice recording stopped', 'Recording Stopped');
    } else {
      // Start recording for English - reset the current session transcript
      this.currentVoiceTranscript = '';
      this.hasNewVoiceText = false;
      this.currentRecordingField = 'en';
      
      try {
        await this.azureSpeechService.startRecognition('en-US');
        this.isRecording = true;
        this.toasterService.showSuccess('Speak now in English... Click stop when finished.', 'Recording Started');
      } catch (error) {
        console.error('Error starting voice recognition:', error);
        this.toasterService.showError('Failed to start voice recognition', 'Error');
        this.isRecording = false;
        this.currentRecordingField = null;
      }
    }
  }

  // Toggle voice input for Arabic
  async toggleVoiceInputArabic(): Promise<void> {
    if (!this.isVoiceSupported || !this.azureSpeechService.isAvailable()) {
      this.toasterService.showError('Azure Speech Service is not available. Please check configuration.', 'Not Supported');
      return;
    }

    // If recording for English, stop it first
    if (this.isRecording) {
      await this.azureSpeechService.stopRecognition();
      this.isRecording = false;
    }

    if (this.isRecordingArabic) {
      // Stop recording
      await this.azureSpeechService.stopRecognition();
      this.isRecordingArabic = false;
      this.currentRecordingField = null;
      this.toasterService.showInfo('Voice recording stopped', 'Recording Stopped');
    } else {
      // Start recording for Arabic - reset the current session transcript
      this.currentVoiceTranscriptAr = '';
      this.hasNewVoiceTextAr = false;
      this.currentRecordingField = 'ar';
      
      try {
        await this.azureSpeechService.startRecognition('ar-SA'); // Arabic (Saudi Arabia)
        this.isRecordingArabic = true;
        this.toasterService.showSuccess('تحدث الآن بالعربية... اضغط إيقاف عند الانتهاء.', 'Recording Started');
      } catch (error) {
        console.error('Error starting voice recognition:', error);
        this.toasterService.showError('Failed to start voice recognition', 'Error');
        this.isRecordingArabic = false;
        this.currentRecordingField = null;
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.agendaItemsList) {
      setTimeout(() => {
        this.scrollToActiveItem();
        this.shouldScroll = false;
      }, 0);
    }
  }

  scrollToActiveItem(): void {
    if (!this.agendaItemsList) return;
    
    const container = this.agendaItemsList.nativeElement;
    const activeItem = container.querySelector(`.agenda-item-tab.active`);
    
    if (activeItem) {
      // Scroll the active item into view, centered horizontally
      activeItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
      
      // Also ensure the container scrolls to show the active item
      // Calculate the position to center the active item
      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      const scrollLeft = container.scrollLeft + (itemRect.left - containerRect.left) - (containerRect.width / 2) + (itemRect.width / 2);
      
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }

  executeEditorCommand(
    editor: HTMLDivElement,
    controlName: 'tcDecisionEn' | 'tcDecisionAr',
    command: string,
    value?: string
  ): void {
    editor.focus();
    document.execCommand(command, false, value);
    this.updateEditorFormControl(controlName, editor);
  }

  onEditorInput(controlName: 'tcDecisionEn' | 'tcDecisionAr', editor: HTMLDivElement): void {
    this.updateEditorFormControl(controlName, editor);
  }

  onEditorPaste(event: ClipboardEvent, controlName: 'tcDecisionEn' | 'tcDecisionAr', editor: HTMLDivElement): void {
    const pastedText = event.clipboardData?.getData('text/plain');

    if (!pastedText) {
      return;
    }

    event.preventDefault();
    editor.focus();
    document.execCommand('insertText', false, this.normalizePastedPlainText(pastedText));
    this.updateEditorFormControl(controlName, editor);
  }

  onEditorBlur(controlName: 'tcDecisionEn' | 'tcDecisionAr'): void {
    this.decisionForm.get(controlName)?.markAsTouched();
  }

  hasEditorText(controlName: 'tcDecisionEn' | 'tcDecisionAr'): boolean {
    return this.getEditorPlainText(controlName).length > 0;
  }

  isEditorEmpty(controlName: 'tcDecisionEn' | 'tcDecisionAr'): boolean {
    return !this.hasEditorText(controlName);
  }

  private updateEditorFormControl(controlName: 'tcDecisionEn' | 'tcDecisionAr', editor: HTMLDivElement): void {
    const normalizedHtml = this.normalizeEditorHtml(editor.innerHTML);
    this.decisionForm.get(controlName)?.setValue(normalizedHtml);
    this.decisionForm.get(controlName)?.markAsDirty();
  }

  private syncEditorViews(): void {
    this.setEditorContent(this.tcDecisionEnEditor, this.decisionForm.get('tcDecisionEn')?.value || '');
    this.setEditorContent(this.tcDecisionArEditor, this.decisionForm.get('tcDecisionAr')?.value || '');
  }

  private syncEditorViewsSoon(): void {
    setTimeout(() => this.syncEditorViews(), 0);
  }

  private setEditorContent(editorRef: ElementRef<HTMLDivElement> | undefined, html: string): void {
    if (!editorRef?.nativeElement) {
      return;
    }

    if (editorRef.nativeElement.innerHTML !== html) {
      editorRef.nativeElement.innerHTML = html;
    }
  }

  private getEditorPlainText(controlName: 'tcDecisionEn' | 'tcDecisionAr'): string {
    const html = this.decisionForm.get(controlName)?.value || '';
    return this.getPlainTextFromHtml(html);
  }

  private normalizeEditorHtml(html: string): string {
    return this.getPlainTextFromHtml(html) ? html : '';
  }

  private normalizePastedPlainText(text: string): string {
    return text
      .replace(/\r\n?/g, '\n')
      .replace(/\u00a0/g, ' ')
      .replace(/\t/g, ' ')
      .split('\n')
      .map(line => line.replace(/[ ]{2,}/g, ' ').trimEnd())
      .join('\n')
      .trim();
  }

  private getPlainTextFromHtml(html: string): string {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    return (tempElement.textContent || tempElement.innerText || '').replace(/\u00a0/g, ' ').trim();
  }

  private appendPlainTextToEditorHtml(html: string, text: string): string {
    const escapedText = this.escapeHtml(text);
    return this.getPlainTextFromHtml(html) ? `${html} ${escapedText}` : escapedText;
  }

  private escapeHtml(text: string): string {
    const tempElement = document.createElement('div');
    tempElement.textContent = text;
    return tempElement.innerHTML;
  }

  get currentAgendaItem(): any {
    return this.agendaItems[this.currentAgendaIndex] || null;
  }

  getAgendaDescriptionEN(): string {
    return this.resolveAgendaDescription(this.currentAgendaItem, 'en');
  }

  getAgendaDescriptionAR(): string {
    return this.resolveAgendaDescription(this.currentAgendaItem, 'ar');
  }

  private resolveAgendaDescription(agendaItem: any, language: 'en' | 'ar'): string {
    if (!agendaItem) {
      return '';
    }

    const keys = language === 'en'
      ? ['descriptionEnglish', 'DescriptionEnglish', 'agendaDescriptionEN', 'AgendaDescriptionEN', 'agendaDescriptionEn', 'descriptionEN', 'DescriptionEN']
      : ['descriptionArabic', 'DescriptionArabic', 'agendaDescriptionAR', 'AgendaDescriptionAR', 'agendaDescriptionAr', 'descriptionAR', 'DescriptionAR'];

    for (const key of keys) {
      const value = agendaItem[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }

    return '';
  }

  get hasNext(): boolean {
    return this.currentAgendaIndex < this.agendaItems.length - 1;
  }

  get hasPrevious(): boolean {
    return this.currentAgendaIndex > 0;
  }

  selectAgendaItem(index: number, broadcast: boolean = true): void {
    // If trying to navigate to the same item, do nothing
    if (index === this.currentAgendaIndex) {
      return;
    }

    // Allow going back (to previous agenda items) without restriction
    if (index < this.currentAgendaIndex) {
      this.performAgendaNavigation(index, broadcast);
      return;
    }

    // If trying to go forward (to next agenda items)
    if (index > this.currentAgendaIndex) {
      // Only allow clicking on the immediate next agenda item (index === currentAgendaIndex + 1)
      // Cannot skip ahead to future agenda items
      if (index > this.currentAgendaIndex + 1) {
        // Show warning message - cannot skip ahead
        this.dialog.open(MessageDialogComponent, {
          width: '450px',
          data: {
            title: 'Cannot Skip Ahead',
            message: `Cannot navigate to agenda item ${index + 1}. You can only navigate to the next agenda item (${this.currentAgendaIndex + 2}) after all members have answered the current agenda item.`,
            type: 'warning'
          }
        });
        return; // Prevent navigation
      }

      // If trying to go to the immediate next agenda item, check if all agenda requirements are complete
      if (index === this.currentAgendaIndex + 1) {
        if (!this.canMoveForwardFromCurrentAgenda) {
          this.showForwardNavigationBlockedDialog('Cannot Navigate Forward', `navigate to agenda item ${index + 1}`);
          return; // Prevent navigation
        }
      }
    }

    // Perform navigation
    this.performAgendaNavigation(index, broadcast);
  }

    // Perform the actual agenda navigation
  private performAgendaNavigation(index: number, broadcast: boolean): void {
    // Save current decision to local map (session/memory only) before switching
    this.agendaDecisions.set(this.currentAgendaIndex, {
      tcDecisionEn: this.decisionForm.value.tcDecisionEn,
      tcDecisionAr: this.decisionForm.value.tcDecisionAr
    });
    
    // Switch to new agenda item
    this.currentAgendaIndex = index;
    this.shouldScroll = true;
    
    // Load attachments for the new agenda item
    this.loadAttachmentsForCurrentAgenda();
    
    // Load saved decision for new agenda item if exists
    const savedDecision = this.agendaDecisions.get(this.currentAgendaIndex);
    const agendaItem = this.agendaItems[this.currentAgendaIndex];
    
    // Check if we should use saved decision or auto-populate from agenda item data
    if (savedDecision && (savedDecision.tcDecisionEn || savedDecision.tcDecisionAr)) {
      // Use saved decision if it has values
      this.decisionForm.patchValue({
        tcDecisionEn: savedDecision.tcDecisionEn || '',
        tcDecisionAr: savedDecision.tcDecisionAr || ''
      });
    } else if (agendaItem && (agendaItem.tcDecisionEN || agendaItem.tcDecisionAR)) {
      // Auto-populate from agenda item data if available
      this.decisionForm.patchValue({
        tcDecisionEn: agendaItem.tcDecisionEN || '',
        tcDecisionAr: agendaItem.tcDecisionAR || ''
      });
      
      // Save to agendaDecisions map
      this.agendaDecisions.set(this.currentAgendaIndex, {
        tcDecisionEn: agendaItem.tcDecisionEN || '',
        tcDecisionAr: agendaItem.tcDecisionAR || ''
      });
    } else {
      // Clear form if no data available
      this.decisionForm.patchValue({
        tcDecisionEn: '',
        tcDecisionAr: ''
      });
    }

    // Load members answers for the new agenda item
    this.loadMembersAnswersForCurrentAgenda();
    this.loadUnsealBidsForCurrentAgenda();
    this.signalRService.getAgendaWorkflowActions(this.meetingId, this.currentAgendaIndex);
    this.syncEvaluationAssignmentContext();

    // Broadcast agenda navigation to all users
    if (broadcast && this.meetingId) {
      this.signalRService.navigateAgenda(this.meetingId, index);
    }
  }

  // Auto-populate TC Decisions from agenda item data (from API response)
  private autoPopulateTCDecisions(agendaIndex: number): void {
    console.log('Auto-populate TC Decisions called for index:', agendaIndex);
    const agendaItem = this.agendaItems[agendaIndex];
    if (!agendaItem) {
      console.log('No agenda item found at index:', agendaIndex);
      return;
    }

    console.log('Agenda item data:', {
      id: agendaItem.id,
      subject: agendaItem.subject,
      tcDecisionEN: agendaItem.tcDecisionEN,
      tcDecisionAR: agendaItem.tcDecisionAR,
      allKeys: Object.keys(agendaItem)
    });

    // Get TC Decisions from agenda item data (from API response)
    // The API returns tcDecisionEN and tcDecisionAR (camelCase with capitals)
    const tcDecisionEN = agendaItem.tcDecisionEN || agendaItem.tcDecisionEn || '';
    const tcDecisionAR = agendaItem.tcDecisionAR || agendaItem.tcDecisionAr || '';

    console.log('Extracted TC Decisions:', { tcDecisionEN, tcDecisionAR });

    // Auto-populate if TC Decisions exist
    if (tcDecisionEN || tcDecisionAR) {
      const valuesToSet: any = {};
      
      if (tcDecisionEN) {
        valuesToSet.tcDecisionEn = tcDecisionEN;
      }
      if (tcDecisionAR) {
        valuesToSet.tcDecisionAr = tcDecisionAR;
      }

      console.log('Setting form values:', valuesToSet);
      this.decisionForm.patchValue(valuesToSet);
      
      // Also save to agendaDecisions map so it persists during navigation
      this.agendaDecisions.set(agendaIndex, {
        tcDecisionEn: tcDecisionEN,
        tcDecisionAr: tcDecisionAR
      });
      
      console.log('Form values after patch:', {
        tcDecisionEn: this.decisionForm.get('tcDecisionEn')?.value,
        tcDecisionAr: this.decisionForm.get('tcDecisionAr')?.value
      });
    } else {
      console.log('No TC Decisions found in agenda item');
    }
  }

  goToPrevious(): void {
    if (this.hasPrevious) {
      this.selectAgendaItem(this.currentAgendaIndex - 1, true); // Broadcast navigation
    }
  }

  goToNext(): void {
    if (this.hasNext) {
      // Check if all agenda requirements are complete before allowing navigation
      if (!this.canMoveForwardFromCurrentAgenda) {
        this.showForwardNavigationBlockedDialog('Cannot Navigate Forward', 'navigate to the next agenda item');
        return; // Prevent navigation
      }

      // Save current decision to local map (session/memory only)
      this.agendaDecisions.set(this.currentAgendaIndex, {
        tcDecisionEn: this.decisionForm.value.tcDecisionEn,
        tcDecisionAr: this.decisionForm.value.tcDecisionAr
      });
      
      this.selectAgendaItem(this.currentAgendaIndex + 1, true); // Broadcast navigation
    }
  }

  // Finish the meeting (same validation as Next, but ends the meeting)
  async finishMeeting(): Promise<void> {
    // Check if all agenda requirements are complete before allowing to finish
    if (!this.canMoveForwardFromCurrentAgenda) {
      this.showForwardNavigationBlockedDialog('Cannot Finish Meeting', 'finish the meeting');
      return; // Prevent finishing
    }

    // If validation passes, end the meeting
    await this.endMeeting();
  }

  onCancel(): void {
    // Minimize instead of close
    this.minimizeDialog();
  }

  minimizeDialog(): void {
    this.agendaDecisions.set(this.currentAgendaIndex, {
      tcDecisionEn: this.decisionForm.get('tcDecisionEn')?.value || '',
      tcDecisionAr: this.decisionForm.get('tcDecisionAr')?.value || ''
    });

    this.isMinimizing = true;
    
    // Get meeting status from data or assume OnGoing for admin dialog
    const status = this.data?.meetingStatus || 'OnGoing';
    
    // Add to minimized meetings
    this.minimizedMeetingService.addMinimizedMeeting({
      meetingId: this.meetingId,
      meetingTitle: this.meetingTitle,
      status: status,
      dialogType: 'admin',
      dialogData: {
        meetingTitle: this.meetingTitle,
        meetingId: this.meetingId,
        meetingIdInt: this.data?.meetingIdInt || this.data?.id,
        id: this.data?.meetingIdInt || this.data?.id,
        agendaItems: this.agendaItems,
        currentAgendaIndex: this.currentAgendaIndex,
        meetingStatus: status,
        meetingMembers: this.meetingMembers,
        attendees: this.attendees,
        agendaDecisions: Array.from(this.agendaDecisions.entries()).map(([agendaIndex, decision]) => ({
          agendaIndex,
          ...decision
        })),
        membersAnswers: Array.from(this.membersAnswers.entries()).map(([agendaIndex, answers]) => ({
          agendaIndex,
          answers
        }))
      },
      dialogRef: this.dialogRef
    });

    // Close the dialog (will be restored from minimized state)
    this.dialogRef.close({ action: 'minimized' });
  }

  async endMeeting(): Promise<void> {
    // Save current decision
    this.agendaDecisions.set(this.currentAgendaIndex, {
      tcDecisionEn: this.decisionForm.value.tcDecisionEn,
      tcDecisionAr: this.decisionForm.value.tcDecisionAr
    });

    // Collect all decisions
    const decisions = Array.from(this.agendaDecisions.entries()).map(([index, decision]) => ({
      agendaIndex: index,
      agendaItem: this.agendaItems[index],
      ...decision
    }));

    console.log('Ending meeting with decisions:', decisions);

    // Get meeting ID (integer) from data
    const meetingIdInt = this.data?.meetingIdInt || this.data?.id;

    if (!meetingIdInt) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: 'Meeting ID is missing. Cannot end meeting.',
          type: 'error'
        }
      });
      return;
    }

    this._loaderService.showLoader();
    try {
        // Collect all members' answers for all agenda items before showing summary
        const allAgendaAnswers: Map<number, Array<{ userId: string; userName: string; action: string; comment: string }>> = new Map();
        
        // Request answers for each agenda item and wait for responses
        const answerPromises: Promise<void>[] = [];
        for (let i = 0; i < this.agendaItems.length; i++) {
          answerPromises.push(
            new Promise<void>((resolve) => {
              // Subscribe to the answer for this agenda item
              const sub = this.signalRService.allMembersAnswers$.subscribe(
                (data) => {
                  if (data.meetingId === this.meetingId && data.agendaIndex === i) {
                    allAgendaAnswers.set(i, data.answers);
                    sub.unsubscribe();
                    resolve();
                  }
                }
              );
              
              // Request the answers
              this.signalRService.getAllMembersAnswers(this.meetingId, i);
              
              // Allow slower SignalR connections enough time to return larger meeting recaps.
              setTimeout(() => {
                sub.unsubscribe();
                resolve();
              }, 10000);
            })
          );
        }

        // Wait for all answers to be collected
        await Promise.all(answerPromises);

        // Also use the already collected membersAnswers as fallback
        this.agendaItems.forEach((_, index) => {
          if (!allAgendaAnswers.has(index) && this.membersAnswers.has(index)) {
            allAgendaAnswers.set(index, this.membersAnswers.get(index)!);
          }
        });

        // Prepare data to save
        // 1. Member Attendances - get from meeting members and attendees
        const memberAttendances: any[] = [];
        // Get meeting members from data or fetch from API
        const meetingMembers = this.data?.meetingMembers || [];
        
        console.log('Collecting member attendances:', {
          meetingMembersCount: meetingMembers.length,
          attendeesCount: this.attendees.length,
          meetingMembers: meetingMembers,
          attendees: this.attendees
        });
        
        // For each member, check if they attended (are in attendees list)
        meetingMembers.forEach((member: any) => {
          // MeetingMemberDTO has: UserId, UserName, UserEmail, Role
          // Frontend maps it to: userId, name, email, role
          const userId = member.userId || member.UserId || member.id || member.userIdInt || member.user?.id || member.user?.userId;
          if (!userId) {
            console.warn('Member missing userId, skipping:', member);
            return;
          }
          
          const hasAttended = this.attendees.some(a => 
            a.userId?.toString() === userId?.toString() || 
            (a.userName && (member.name || member.UserName) && a.userName.toLowerCase() === (member.name || member.UserName).toLowerCase())
          );
          
          const role = member.role || member.Role || 'Member';
          
          memberAttendances.push({
            userId: parseInt(userId),
            attended: hasAttended,
            role: role
          });
        });
        
        console.log('Member attendances prepared:', memberAttendances);

        // 2. Agenda Decisions - Collect ALL decisions from the session map
        // This ensures even decisions that weren't broadcasted are saved
        const agendaDecisions: any[] = [];
        
        // Iterate through ALL agenda items to ensure complete data
        this.agendaItems.forEach((agendaItem, index) => {
          const agendaId = agendaItem?.id || agendaItem?.agendaItemId || agendaItem?.Id;
          
          if (!agendaId) {
            console.warn(`Skipping agenda item at index ${index} - no valid ID found`);
            return;
          }
          
          // Get decision from the decisions map (already collected from agendaDecisions)
          const decision = this.agendaDecisions.get(index);
          
          // Only add if there's actual decision data (EN or AR)
          if (decision && (decision.tcDecisionEn || decision.tcDecisionAr)) {
            agendaDecisions.push({
              agendaId: agendaId,
              tcDecisionEN: decision.tcDecisionEn || '',
              tcDecisionAR: decision.tcDecisionAr || '',
              requiredFromTenderCommittee: agendaItem?.requiredFromTC || agendaItem?.requiredFromTenderCommittee || ''
            });
          }
        });
        
        console.log('Agenda decisions prepared (all from session):', {
          totalAgendaItems: this.agendaItems.length,
          decisionsWithData: agendaDecisions.length,
          decisions: agendaDecisions
        });

        // 3. Agenda Member Responses
        const agendaMemberResponses: any[] = [];
        allAgendaAnswers.forEach((answers, agendaIndex) => {
          const agendaItem = this.agendaItems[agendaIndex];
          const agendaId = agendaItem?.id || agendaItem?.agendaItemId || 0;
          
          answers.forEach((answer: any) => {
            if (!answer.userId) {
              console.warn('Answer missing userId, skipping:', answer);
              return;
            }
            agendaMemberResponses.push({
              userId: parseInt(answer.userId),
              agendaId: agendaId,
              response: answer.action || '', // "accepted", "rejected", "deferred"
              comment: answer.comment || ''
            });
          });
        });
        
        console.log('Agenda member responses prepared:', agendaMemberResponses);

        // Save all meeting data
        const saveDataUrl = `${environment.API_URL}/api/Meetings/${meetingIdInt}/save-data`;
        const evaluationReviewDecisions = Array.from(this.agendaWorkflowActions.entries()).flatMap(([agendaIndex, actions]) => {
          const agendaId = this.agendaItems[agendaIndex]?.id || this.agendaItems[agendaIndex]?.agendaItemId || this.agendaItems[agendaIndex]?.Id;
          return actions.filter(action => action.actionType === 'evaluation-review').map(action => ({
            agendaId,
            evaluationId: action.entityId,
            isApproved: !!action.isApproved,
            comments: action.comments || null
          }));
        }).filter(decision => !!decision.agendaId);
        const awardDecisions = Array.from(this.agendaWorkflowActions.entries()).flatMap(([agendaIndex, actions]) => {
          const agendaId = this.agendaItems[agendaIndex]?.id || this.agendaItems[agendaIndex]?.agendaItemId || this.agendaItems[agendaIndex]?.Id;
          return actions.filter(action => action.actionType === 'award').map(action => ({ agendaId, bidId: action.entityId }));
        }).filter(decision => !!decision.agendaId);
        const saveDataPayload = {
          memberAttendances: memberAttendances,
          agendaDecisions: agendaDecisions,
          agendaMemberResponses: agendaMemberResponses,
          evaluationReviewDecisions,
          awardDecisions
        };

        console.log('Saving meeting data:', {
          meetingId: meetingIdInt,
          memberAttendancesCount: memberAttendances.length,
          agendaDecisionsCount: agendaDecisions.length,
          agendaMemberResponsesCount: agendaMemberResponses.length,
          payload: saveDataPayload
        });

        try {
          const saveDataResponse: any = await lastValueFrom(this.apiService.postByUrl(saveDataUrl, saveDataPayload));
          if (saveDataResponse?.isSuccess) {
            console.log('Meeting data saved successfully');
          } else {
            console.error('Failed to save meeting data:', saveDataResponse?.errorMessageEn);
            this._loaderService.hideLoader();
            this.dialog.open(MessageDialogComponent, {
              width: '400px',
              data: {
                title: 'Error',
                message: saveDataResponse?.errorMessageEn || 'Failed to save meeting data. Please try again.',
                type: 'error'
              }
            });
            return;
          }
        } catch (error: any) {
          console.error('Error saving meeting data:', error);
          this._loaderService.hideLoader();
          this.dialog.open(MessageDialogComponent, {
            width: '400px',
            data: {
              title: 'Error',
              message: error?.error?.errorMessageEn || 'An error occurred while saving meeting data. Please try again.',
              type: 'error'
            }
          });
          return;
        }

        // Complete only after the recap data has been stored successfully.
        const completeUrl = `${environment.API_URL}/api/Meetings/${meetingIdInt}/complete`;
        const completeResponse: any = await lastValueFrom(this.apiService.postByUrl(completeUrl, {}));
        if (!completeResponse?.isSuccess) {
          console.error('Failed to end meeting:', completeResponse?.errorMessageEn);
          this.dialog.open(MessageDialogComponent, {
            width: '400px',
            data: {
              title: 'Error',
              message: completeResponse?.errorMessageEn || 'Failed to complete the meeting. Please try again.',
              type: 'error'
            }
          });
          return;
        }

        this.minimizedMeetingService.updateMeetingStatus(this.meetingId, 'Completed');
        this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);

        // The complete endpoint broadcasts MeetingEnded and clears the server session.
        // Only show summary dialog if not already opened
        if (!this.summaryDialogOpened) {
          console.log('Meeting ended, showing summary dialog');
          
          // Set flag to prevent opening dialog twice
          this.summaryDialogOpened = true;

          // Show summary dialog for hoster with all members' responses
          setTimeout(() => {
            this.dialog.open(MeetingSummaryDialogComponent, {
              width: '1200px',
              maxWidth: '95vw',
              maxHeight: '95vh',
              disableClose: false,
              panelClass: 'meeting-summary-dialog',
              data: {
                isHoster: true,
                meetingTitle: this.meetingTitle,
                agendaItems: this.agendaItems,
                allAgendaAnswers: Array.from(allAgendaAnswers.entries()).map(([index, answers]) => ({
                  agendaIndex: index,
                  agendaItem: this.agendaItems[index],
                  answers: answers
                })),
                decisions: decisions
              }
            });
          }, 300);
        }

        // Close the dialog
        this.dialogRef.close({ action: 'ended', decisions: decisions });
    } catch (error: any) {
      console.error('Error ending meeting:', error);
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: error?.error?.errorMessageEn || 'An error occurred while ending the meeting. Please try again.',
          type: 'error'
        }
      });
    } finally {
      this._loaderService.hideLoader();
    }
  }

  goBackToAgendaList(): void {
    this.dialogRef.close({ action: 'back' });
  }

  kickUser(userId: string, userName: string): void {
    // Confirm before kicking user
    const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Remove Attendee',
        message: `Are you sure you want to remove ${userName} from this meeting?`,
        confirmText: 'Remove',
        cancelText: 'Cancel'
      }
    });

    confirmDialog.afterClosed().subscribe(result => {
      if (result) {
        const currentUserId = this.currentUserId;
        if (this.meetingId && currentUserId) {
          // Call SignalR to kick the user
          this.signalRService.kickUserFromMeeting(this.meetingId, userId, currentUserId).then(() => {
            this.toasterService.showSuccess(`${userName} has been removed from the meeting.`, 'User Removed');
          }).catch(error => {
            console.error('Error kicking user:', error);
            this.toasterService.showError('Failed to remove user from meeting. Please try again.', 'Error');
          });
        }
      }
    });
  }

  async onTranslateToArabic(): Promise<void> {
    const englishHtml = this.decisionForm.get('tcDecisionEn')?.value || '';
    const englishText = this.getPlainTextFromHtml(englishHtml);
    if (!englishText || englishText.trim() === '') {
      this.toasterService.showWarning('Please enter text in English to translate', 'No Text');
      return;
    }

    this.isTranslating = true;
    try {
      const translatedHtml = await this.translateEditorHtml(englishHtml, 'ar');
      this.decisionForm.patchValue({ tcDecisionAr: translatedHtml }, { emitEvent: false });
      this.syncEditorViews();
      this.toasterService.showSuccess('Text translated successfully', 'Translation Complete');
    } catch (error) {
      console.error('Translation failed:', error);
      this.toasterService.showError('Translation failed. Please try again.', 'Translation Error');
    } finally {
      this.isTranslating = false;
    }
  }

  async onTranslateToEnglish(): Promise<void> {
    const arabicHtml = this.decisionForm.get('tcDecisionAr')?.value || '';
    const arabicText = this.getPlainTextFromHtml(arabicHtml);
    if (!arabicText || arabicText.trim() === '') {
      this.toasterService.showWarning('Please enter text in Arabic to translate', 'No Text');
      return;
    }

    this.isTranslating = true;
    try {
      const translatedHtml = await this.translateEditorHtml(arabicHtml, 'en');
      this.decisionForm.patchValue({ tcDecisionEn: translatedHtml }, { emitEvent: false });
      this.syncEditorViews();
      this.toasterService.showSuccess('Text translated successfully', 'Translation Complete');
    } catch (error) {
      console.error('Translation failed:', error);
      this.toasterService.showError('Translation failed. Please try again.', 'Translation Error');
    } finally {
      this.isTranslating = false;
    }
  }

  private async translateEditorHtml(html: string, targetLanguage: 'ar' | 'en'): Promise<string> {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();

    while (currentNode) {
      const textNode = currentNode as Text;
      if (textNode.textContent && textNode.textContent.trim()) {
        textNodes.push(textNode);
      }
      currentNode = walker.nextNode();
    }

    for (const textNode of textNodes) {
      textNode.textContent = await this.translateEditorTextNode(textNode.textContent || '', targetLanguage);
    }

    return this.normalizeEditorHtml(wrapper.innerHTML);
  }

  private async translateEditorTextNode(text: string, targetLanguage: 'ar' | 'en'): Promise<string> {
    const parts = text.split(/(\n+)/);
    const translatedParts: string[] = [];

    for (const part of parts) {
      if (!part.trim()) {
        translatedParts.push(part);
        continue;
      }

      const leadingWhitespace = part.match(/^\s*/)?.[0] || '';
      const trailingWhitespace = part.match(/\s*$/)?.[0] || '';
      const sourceText = part.trim();
      const translatedText = this.cleanTranslatedText(
        targetLanguage === 'ar'
          ? await lastValueFrom(this.translationService.translateToArabic(sourceText))
          : await lastValueFrom(this.translationService.translateToEnglish(sourceText))
      );

      translatedParts.push(`${leadingWhitespace}${translatedText}${trailingWhitespace}`);
    }

    return translatedParts.join('');
  }

  private cleanTranslatedText(text: string): string {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = text || '';
    return decoder.value
      .replace(/<\/?g\b[^>]*>/gi, '')
      .replace(/<\/?x\b[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '')
      .trim();
  }

  async loadAttachmentsForCurrentAgenda(): Promise<void> {
    const currentAgenda = this.currentAgendaItem;
    if (!currentAgenda) {
      this.currentAgendaItemAttachments = [];
      return;
    }

    // Get agenda item ID (could be id or agendaItemId)
    const agendaItemId = currentAgenda.id || currentAgenda.agendaItemId;
    if (!agendaItemId) {
      this.currentAgendaItemAttachments = [];
      return;
    }

    this.isLoadingAttachments = true;
    this.currentAgendaItemAttachments = [];

    try {
      // Fetch documents for this agenda item
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: agendaItemId.toString(),
        processName: 'AgendaItem',
        processID: agendaItemId.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;
          
          // Use direct blob access (SAS URLs) - same as in create-agenda-item-dialog
          const USE_DIRECT_BLOB_ACCESS = true;
          
          // Process documents and fetch file sizes
          const attachmentPromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name;
              } else {
                return doc.attachmentId && doc.name;
              }
            })
            .map(async (doc: any) => {
              // Format file size
              const formatFileSize = (bytes: number): string => {
                if (!bytes || bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
              };

              // Get file URL - use SAS URL if available, otherwise use download endpoint
              let fileUrl: string;
              if (USE_DIRECT_BLOB_ACCESS && doc.fileContent) {
                fileUrl = doc.fileContent; // SAS URL
              } else {
                fileUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
              }

              // Fetch file to get actual size
              let fileSize = 0;
              try {
                const response = await fetch(fileUrl, { method: 'HEAD' });
                const contentLength = response.headers.get('content-length');
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10);
                } else {
                  // If HEAD doesn't work, fetch the blob to get size
                  const blobResponse = await fetch(fileUrl);
                  const blob = await blobResponse.blob();
                  fileSize = blob.size;
                }
              } catch (error) {
                console.warn(`Could not fetch file size for ${doc.name}:`, error);
                // Try to use doc.size if available as fallback
                fileSize = doc.size || doc.fileSize || 0;
              }

              // Clean filename - remove attachment IDs and UUIDs
              // Backend generates: "{vendorId}-{attachmentFor}-{attachmentId}-{uniqueId}.{extension}"
              // Example: "20-AgendaItemAttachment-07ed1d6e-ab75-488f-9681-b47eb74d6c50-43cd02d0-115e-4c3e-9e63-0f1516ea3c22.pdf"
              // Should become: "document.pdf" (just extension, no IDs)
              let cleanFileName = doc.name || 'document';
              
              // Extract file extension
              const extension = cleanFileName.includes('.') ? cleanFileName.split('.').pop() || '' : '';
              
              // Pattern to match UUIDs (8-4-4-4-12 hex format)
              const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
              
              // Check if filename contains UUIDs (which indicates it has IDs)
              const hasUUIDs = uuidPattern.test(cleanFileName);
              
              if (hasUUIDs) {
                // Filename contains IDs/UUIDs - use clean generic name with extension
                cleanFileName = extension ? `document.${extension}` : 'document';
              }
              // If no UUIDs found, keep original filename as-is

              return {
                name: cleanFileName,
                originalName: doc.name, // Keep original for display
                url: fileUrl,
                size: formatFileSize(fileSize),
                fileType: doc.mimeType || 'application/octet-stream'
              };
            });

          // Wait for all attachments to be processed
          this.currentAgendaItemAttachments = await Promise.all(attachmentPromises);
        }
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
      this.currentAgendaItemAttachments = [];
    } finally {
      this.isLoadingAttachments = false;
    }
  }

  async previewAttachment(attachment: { name: string; url: string; fileType: string }): Promise<void> {
    try {
      // Detect Safari/iPad - Safari blocks window.open() after async operations
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                       /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // For Safari/iPad: Open the original URL directly in a new tab (no blank page)
      if (isSafari) {
        window.open(attachment.url, '_blank', 'noopener,noreferrer');
        return;
      }

      // For Chrome/other browsers: Fetch as blob to prevent downloads
      // Fetch the file as a blob to create an object URL for preview
      // This prevents the browser from downloading the file due to Content-Disposition headers
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const file = new File([blob], attachment.name, { type: attachment.fileType });
      const fileURL = URL.createObjectURL(file);
      
      // Open in new tab with file loaded
      window.open(fileURL, '_blank', 'noopener,noreferrer');
      
      // Don't revoke the blob URL - let browser handle cleanup when tab closes
      // The file-upload component doesn't revoke it either, letting the browser manage it
    } catch (error) {
      console.error('Error previewing attachment:', error);
      this.toasterService.showError('Failed to preview file. Please try downloading instead.', 'Preview Error');
    }
  }

  async downloadAttachment(attachment: { name: string; originalName?: string; url: string; fileType: string }): Promise<void> {
    try {
      // Fetch the file as a blob
      const response = await fetch(attachment.url, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create a File object with the clean filename (without IDs)
      const file = new File([blob], attachment.name, { type: attachment.fileType });
      
      // Create object URL and trigger download with clean filename
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = attachment.name; // Use clean filename (without IDs)
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up object URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 100);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      this.toasterService.showError('Failed to download file. Please try again.', 'Download Error');
    }
  }
}

